"use client";

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { DealerInfo, VehicleDetails } from "@/types/vehicle-details";
import { VehicleSaveIconButton } from "./VehicleActions";
import { buildVehicleGallery } from "@/utils/galleryPromotions";
import { useGalleryPromotions } from "@/components/cars/GalleryPromotionsProvider";
import { getGallerySwipeDirection } from "@/utils/vehicleGallery";
import styles from "../VehicleDetailsPage.module.css";

export interface VehicleGalleryProps {
    images: string[];
    vehicleName: string;
    vehicleData: VehicleDetails;
    dealerData: DealerInfo | null;
    numberOfPhotos?: number;
}

export default function VehicleGallery({
    images,
    vehicleName,
    vehicleData,
    dealerData
}: VehicleGalleryProps) {
    const galleryId = useId();
    const openerRef = useRef<HTMLButtonElement | null>(null);
    const [photoError, setPhotoError] = useState(false);
    const openGallery = (button: HTMLButtonElement) => {
        openerRef.current = button;
        setShowLightbox(true);
    };
    const hasVehicleImages = images.length > 0;
    const promotions = useGalleryPromotions();
    const displayImages = useMemo(() => buildVehicleGallery(images, promotions, vehicleName, { price: vehicleData.price, make: vehicleData.make, model: vehicleData.model }), [images, promotions, vehicleName, vehicleData.price, vehicleData.make, vehicleData.model]);
    const previewImages = hasVehicleImages ? displayImages.slice(0, 4) : [];
    const photoCount = displayImages.length;
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [visibleIndex, setVisibleIndex] = useState(0);
    const [visitedIndices, setVisitedIndices] = useState([0]);
    const loadedImages = useRef(new Set<string>());
    const requestedIndex = useRef(0);
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
    const suppressImageClickRef = useRef(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const photoRefs = useRef<(HTMLElement | null)[]>([]);

    const selectPhoto = useCallback((index: number) => {
        setPhotoError(false);
        requestedIndex.current = index;
        setSelectedIndex(index);
        setVisitedIndices(current => current.includes(index) ? current : [...current, index]);
        if (loadedImages.current.has(displayImages[index]!.src)) setVisibleIndex(index);
    }, [displayImages]);

    const selectPrevious = () => selectPhoto((selectedIndex + displayImages.length - 1) % displayImages.length);
    const selectNext = () => selectPhoto((selectedIndex + 1) % displayImages.length);

    const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (!event.isPrimary || event.pointerType === "mouse") return;
        suppressImageClickRef.current = false;
        pointerStartRef.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
        const start = pointerStartRef.current;
        pointerStartRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!start || displayImages.length <= 1) return;
        const direction = getGallerySwipeDirection({
            startX: start.x, startY: start.y, endX: event.clientX, endY: event.clientY
        });
        if (!direction) return;
        suppressImageClickRef.current = true;
        if (direction === "next") selectNext();
        else selectPrevious();
    };

    useEffect(() => {
        if (!showLightbox) return;
        const dialog = dialogRef.current;
        const scroller = scrollRef.current;
        const previousFocus = openerRef.current;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        dialog?.showModal();
        const photo = photoRefs.current[selectedIndex];
        if (scroller && photo) scroller.scrollTop = photo.offsetTop;

        return () => {
            dialog?.close();
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus({ preventScroll: true });
        };
    }, [showLightbox, selectedIndex]);

    return (
        <>
            <div className={styles["gallery"]} role="group" aria-roledescription="carousel" aria-label={`${vehicleName} photos`}>
                <div className={styles["mainPhoto"]}>
                    <button
                        type="button"
                        className={styles["mainPhotoImageButton"]}
                        onPointerDown={handlePointerDown}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={() => { pointerStartRef.current = null; }}
                        onClick={event => {
                            if (suppressImageClickRef.current && event.detail !== 0) {
                                suppressImageClickRef.current = false;
                                return;
                            }
                            openGallery(event.currentTarget);
                        }}
                        aria-haspopup="dialog"
                        aria-label={`Open all ${photoCount} photos of ${vehicleName}`}
                        aria-describedby={`${galleryId}-status`}>
                        {visitedIndices.map(index => {
                            const image = displayImages[index];
                            if (!image) return null;
                            return <Image
                                key={image.id}
                                src={image.src}
                                alt={index === visibleIndex ? image.alt : ""}
                                aria-hidden={index !== visibleIndex}
                                fill
                                className={`${styles["mainPhotoImage"]} ${index === visibleIndex ? styles["mainPhotoImageVisible"] : ""}`}
                                style={image.kind === "promotion" ? { objectFit: "contain", backgroundColor: "#16121d" } : undefined}
                                priority={index === 0}
                                loading={index === 0 ? undefined : "eager"}
                                {...(index === 0 ? { fetchPriority: "high" as const } : {})}
                                sizes="(max-width: 850px) 100vw, (max-width: 1100px) calc(100vw - 404px), calc(100vw - 500px)"
                                onError={() => { if (requestedIndex.current === index) setPhotoError(true); }}
                                onLoad={() => {
                                    loadedImages.current.add(image.src);
                                    if (requestedIndex.current === index) { setVisibleIndex(index); setPhotoError(false); }
                                }}
                            />;
                        })}
                    </button>

                    <VehicleSaveIconButton vehicleData={vehicleData} dealerData={dealerData} />

                    {displayImages.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={selectPrevious}
                                className={`${styles["arrow"]} ${styles["previous"]}`}
                                aria-label="Previous photo">
                                <ChevronLeft aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                onClick={selectNext}
                                className={`${styles["arrow"]} ${styles["next"]}`}
                                aria-label="Next photo">
                                <ChevronRight aria-hidden="true" />
                            </button>
                        </>
                    )}

                    <button
                        type="button"
                        onClick={event => openGallery(event.currentTarget)}
                        aria-haspopup="dialog"
                        className={styles["viewAll"]}>
                        View all {photoCount} {photoCount === 1 ? "photo" : "photos"}
                    </button>
                </div>

                {previewImages.length > 0 && (
                    <div className={styles["thumbnails"]} role="group" aria-label="Photo thumbnails">
                        {previewImages.map((image, index) => {
                            const opensAll = index === 3 && displayImages.length > 4;

                            return (
                                <button
                                    key={image.id}
                                    type="button"
                                    onClick={event => {
                                        selectPhoto(index);
                                        if (opensAll) openGallery(event.currentTarget);
                                    }}
                                    className={`${styles["thumbnail"]} ${index === selectedIndex ? styles["thumbnailActive"] : ""}`}
                                    aria-pressed={opensAll ? undefined : index === visibleIndex}
                                    aria-haspopup={opensAll ? "dialog" : undefined}
                                    aria-label={opensAll ? `View all ${photoCount} photos` : `View ${image.alt}`}>
                                    <Image
                                        src={image.src}
                                        alt=""
                                        fill
                                        className={styles["thumbnailImage"]}
                                        style={image.kind === "promotion" ? { objectFit: "contain", backgroundColor: "#16121d" } : undefined}
                                        loading="lazy"
                                        sizes="(max-width: 850px) 104px, 18vw"
                                    />
                                    {opensAll && (
                                        <span className={styles["thumbnailMore"]}>View all {photoCount}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
                <p id={`${galleryId}-status`} className={styles["galleryStatus"]} role="status" aria-live="polite" aria-atomic="true">
                    {photoError ? "That photo could not be loaded. Try another photo. " : ""}
                    Photo {visibleIndex + 1} of {photoCount}: {displayImages[visibleIndex]?.alt}
                </p>
            </div>

            {showLightbox && (
                <dialog
                    ref={dialogRef}
                    className={styles["lightbox"]}
                    aria-label={`${vehicleName} photo gallery`}
                    aria-modal="true"
                    onClose={() => setShowLightbox(false)}
                    onKeyDown={event => {
                        if (event.key !== "Tab") return;
                        if (event.shiftKey && document.activeElement === closeRef.current) {
                            event.preventDefault();
                            scrollRef.current?.focus({ preventScroll: true });
                        } else if (!event.shiftKey && document.activeElement === scrollRef.current) {
                            event.preventDefault();
                            closeRef.current?.focus({ preventScroll: true });
                        }
                    }}
                    onCancel={() => setShowLightbox(false)}
                    onClick={event => { if (event.target === event.currentTarget) setShowLightbox(false); }}>
                    <header className={styles["lightboxHeader"]}>
                        <div><strong>{vehicleName}</strong></div>
                        <button ref={closeRef} type="button" onClick={() => setShowLightbox(false)} className={styles["lightboxClose"]} aria-label="Close photo gallery" autoFocus>
                            <X aria-hidden="true" />
                        </button>
                    </header>
                    <div ref={scrollRef} className={styles["lightboxScroll"]} role="region" tabIndex={0} aria-label="Vehicle photos, scroll vertically">
                        {displayImages.map((image, index) => (
                            <figure key={image.id} ref={element => { photoRefs.current[index] = element; }} className={styles["lightboxPhoto"]}>
                                <div className={styles["lightboxImageWrap"]}>
                                    <Image src={image.src} alt={image.alt} fill className={styles["lightboxImage"]}
                                        loading={index === selectedIndex ? "eager" : "lazy"}
                                        sizes="(max-width: 1000px) 100vw, 1000px" />
                                </div>
                                <figcaption>Photo {index + 1} of {photoCount}</figcaption>
                            </figure>
                        ))}
                    </div>
                </dialog>
            )}
        </>
    );
}
